<template>
  <div>
    <NavBar />
    <main class="page historique-page">
      <section class="tickets-hero">
        <div>
          <p class="eyebrow">Historique des ventes</p>
          <h1 class="page-title">Tickets</h1>
          <p class="page-subtitle">Suivez les ventes fidélité, les ventes sans carte et les règlements par marché.</p>
        </div>
        <div v-if="tickets.length" class="hero-summary" aria-label="Résumé rapide">
          <div>
            <span class="summary-label">Tickets filtrés</span>
            <strong>{{ ticketsFiltres.length }}</strong>
          </div>
          <div>
            <span class="summary-label">CA filtré</span>
            <strong>{{ formatEuro(totalCA) }}</strong>
          </div>
        </div>
      </section>

      <section class="card filters-card" aria-label="Filtres tickets">
        <div class="filters-grid">
          <div class="filtre-group">
            <label class="label">Du</label>
            <input v-model="dateDebut" class="input" type="date" />
          </div>
          <div class="filtre-group">
            <label class="label">Au</label>
            <input v-model="dateFin" class="input" type="date" />
          </div>
          <div class="filtre-group">
            <label class="label">Marché</label>
            <select v-model="villeFiltre" class="input">
              <option value="">Tous les marchés</option>
              <option v-for="v in villesDisponibles" :key="v" :value="v">{{ v }}</option>
            </select>
          </div>
          <div class="filtre-group search-field">
            <label class="label">Rechercher un client</label>
            <input v-model="recherche" class="input" placeholder="Prénom, nom, carte..." />
          </div>
          <div class="filtre-group">
            <label class="label">Trier par</label>
            <select v-model="tri" class="input">
              <option value="date_desc">Date (récent → ancien)</option>
              <option value="date_asc">Date (ancien → récent)</option>
              <option value="nom_asc">Nom A → Z</option>
              <option value="nom_desc">Nom Z → A</option>
              <option value="montant_desc">Montant (plus élevé)</option>
              <option value="points_desc">Points (plus élevé)</option>
            </select>
          </div>
        </div>

        <div class="quick-periods" aria-label="Périodes rapides">
          <button class="period-button" @click="setIntervalle('today')">Aujourd'hui</button>
          <button class="period-button" @click="setIntervalle('week')">Cette semaine</button>
          <button class="period-button" @click="setIntervalle('month')">Ce mois</button>
          <button class="period-button" @click="setIntervalle('all')">Tout</button>
        </div>
      </section>

      <section class="stats-row" aria-label="Indicateurs tickets">
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-ticket" aria-hidden="true"></span>
            <span class="stat-label">Total tickets</span>
          </div>
          <div class="stat-value">{{ ticketsFiltres.length }}</div>
        </div>
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-revenue" aria-hidden="true"></span>
            <span class="stat-label">CA total</span>
          </div>
          <div class="stat-value">{{ formatEuro(totalCA) }}</div>
        </div>
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-points" aria-hidden="true"></span>
            <span class="stat-label">Points distribués</span>
          </div>
          <div class="stat-value">{{ totalPoints }}</div>
        </div>
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-reduction" aria-hidden="true"></span>
            <span class="stat-label">Réductions utilisées</span>
          </div>
          <div class="stat-value">{{ totalReductions }}</div>
        </div>
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-cb" aria-hidden="true"></span>
            <span class="stat-label">CB</span>
          </div>
          <div class="stat-value">{{ formatEuro(totalCB) }}</div>
        </div>
        <div class="stat-card card">
          <div class="stat-meta">
            <span class="stat-icon icon-especes" aria-hidden="true"></span>
            <span class="stat-label">Espèces</span>
          </div>
          <div class="stat-value">{{ formatEuro(totalEspeces) }}</div>
        </div>
      </section>

      <section class="card ca-par-marche" v-if="villesDisponibles.length > 1">
        <div class="section-heading">
          <h2>CA par marché</h2>
          <span>{{ ticketsFiltresSansVille.length }} tickets sur la période</span>
        </div>
        <div class="marches-stats">
          <button class="marche-stat" v-for="v in villesDisponibles" :key="v"
            :class="{ actif: villeFiltre === v }"
            @click="villeFiltre = villeFiltre === v ? '' : v"
          >
            <span class="ms-ville">{{ v }}</span>
            <strong class="ms-ca">{{ formatEuro(caParVille(v)) }}</strong>
            <span class="ms-tickets">{{ ticketsParVille(v) }} tickets</span>
          </button>
          <button class="marche-stat total" @click="villeFiltre = ''">
            <span class="ms-ville">Total</span>
            <strong class="ms-ca">{{ formatEuro(totalCA) }}</strong>
            <span class="ms-tickets">{{ ticketsFiltresSansVille.length }} tickets</span>
          </button>
        </div>
      </section>

      <section class="card tickets-card">
        <div class="section-heading tickets-heading">
          <div>
            <h2>Liste des tickets</h2>
            <span>{{ ticketsFiltres.length }} résultat{{ ticketsFiltres.length > 1 ? 's' : '' }}</span>
          </div>
        </div>

        <div v-if="loading" class="state-box loading">
          <span class="loader" aria-hidden="true"></span>
          <strong>Chargement des tickets</strong>
          <p>Récupération de l'historique des ventes en cours.</p>
        </div>
        <div v-else-if="erreur" class="state-box error-state">
          <strong>Historique indisponible</strong>
          <p>{{ erreur }}</p>
        </div>
        <div v-else-if="ticketsFiltres.length === 0" class="state-box vide">
          <strong>Aucun ticket trouvé</strong>
          <p>Aucune vente ne correspond aux filtres sélectionnés.</p>
        </div>
        <div v-else>
          <div v-if="erreurReglement" class="inline-error" role="alert">
            {{ erreurReglement }}
          </div>

          <div class="table-container desktop-table">
            <table class="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Carte</th>
                  <th>Marché</th>
                  <th class="align-right">Montant</th>
                  <th>Points gagnés</th>
                  <th>Points utilisés</th>
                  <th>Solde actuel</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Règlement</th>
                  <th>Changer règlement</th>
                  <th class="align-right">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in ticketsFiltres" :key="ticketKey(t)" :class="{ 'row-sans-carte': t.source === 'sans_carte' }">
                  <td class="client-cell">
                    <div class="client-avatar-sm" :class="{ 'avatar-muted': t.source === 'sans_carte' }">{{ clientInitiales(t) }}</div>
                    <div>
                      <strong>{{ clientNom(t) }}</strong>
                      <span v-if="t.source === 'sans_carte'" class="client-note">Vente non rattachée à une carte</span>
                    </div>
                  </td>
                  <td class="mono">{{ t.numero_carte || '—' }}</td>
                  <td>
                    <span class="badge-ville" v-if="t.ville">{{ t.ville }}</span>
                    <span class="muted-value" v-else>—</span>
                  </td>
                  <td class="montant align-right">{{ formatEuro(t.montant || t.montant_achat) }}</td>
                  <td class="points-plus">{{ t.points_gagnes > 0 ? '+' + t.points_gagnes + ' pts' : '—' }}</td>
                  <td class="points-moins">{{ t.points_utilises > 0 ? '-' + t.points_utilises + ' pts' : '—' }}</td>
                  <td>
                    <span class="badge badge-green" v-if="t.points_solde !== null">{{ t.points_solde }} pts</span>
                    <span class="muted-value" v-else>—</span>
                  </td>
                  <td class="date">{{ formatDate(t.created_at) }}</td>
                  <td><span class="badge" :class="badgeType(t.type)">{{ labelType(t.type) }}</span></td>
                  <td>
                    <span class="badge-paiement" :class="classeModePaiement(t)">
                      {{ labelModePaiement(t.mode_paiement) }}
                    </span>
                  </td>
                  <td>
                    <button
                      class="badge-paiement paiement-button"
                      :class="classeModePaiement(t)"
                      :disabled="reglementEnCours === ticketKey(t)"
                      :title="libelleActionReglement(t)"
                      :aria-label="libelleActionReglement(t)"
                      @click="changerReglement(t)"
                    >
                      {{ libelleBoutonReglement(t) }}
                    </button>
                  </td>
                  <td class="align-right">
                    <button
                      class="ticket-action"
                      :disabled="annulationEnCours === ticketKey(t)"
                      @click="annulerTicket(t)"
                    >
                      {{ annulationEnCours === ticketKey(t) ? 'Annulation...' : 'Annuler' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="tickets-mobile" aria-label="Tickets">
            <article class="ticket-mobile-card" v-for="t in ticketsFiltres" :key="'mobile-' + ticketKey(t)" :class="{ 'sans-carte-card': t.source === 'sans_carte' }">
              <div class="ticket-mobile-top">
                <div class="client-cell">
                  <div class="client-avatar-sm" :class="{ 'avatar-muted': t.source === 'sans_carte' }">{{ clientInitiales(t) }}</div>
                  <div>
                    <strong>{{ clientNom(t) }}</strong>
                    <span class="client-note">{{ t.source === 'sans_carte' ? 'Vente sans carte' : (t.numero_carte || 'Carte non renseignée') }}</span>
                  </div>
                </div>
                <div class="mobile-amount">{{ formatEuro(t.montant || t.montant_achat) }}</div>
              </div>
              <div class="ticket-mobile-grid">
                <div>
                  <span>Marché</span>
                  <strong>{{ t.ville || '—' }}</strong>
                </div>
                <div>
                  <span>Date</span>
                  <strong>{{ formatDate(t.created_at) }}</strong>
                </div>
                <div class="reglement-mobile-field">
                  <span>Règlement</span>
                  <strong>
                    <span class="badge-paiement" :class="classeModePaiement(t)">
                      {{ labelModePaiement(t.mode_paiement) }}
                    </span>
                  </strong>
                </div>
                <div class="reglement-mobile-action">
                  <span>Changer règlement</span>
                  <button
                    class="badge-paiement paiement-button mobile-paiement-button"
                    :class="classeModePaiement(t)"
                    :disabled="reglementEnCours === ticketKey(t)"
                    :title="libelleActionReglement(t)"
                    :aria-label="libelleActionReglement(t)"
                    @click="changerReglement(t)"
                  >
                    {{ libelleBoutonReglement(t) }}
                  </button>
                </div>
                <div>
                  <span>Points</span>
                  <strong class="points-plus">{{ t.points_gagnes > 0 ? '+' + t.points_gagnes + ' pts' : '—' }}</strong>
                </div>
              </div>
              <div class="ticket-mobile-footer">
                <span class="badge" :class="badgeType(t.type)">{{ labelType(t.type) }}</span>
                <span v-if="t.points_utilises > 0" class="points-moins">-{{ t.points_utilises }} pts utilisés</span>
              </div>
              <button
                class="ticket-action mobile-action"
                :disabled="annulationEnCours === ticketKey(t)"
                @click="annulerTicket(t)"
              >
                {{ annulationEnCours === ticketKey(t) ? 'Annulation...' : 'Annuler le ticket' }}
              </button>
            </article>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'

export default {
  name: 'HistoriqueView',
  components: { NavBar },
  data() {
    return {
      tickets:     [],
      recherche:   '',
      tri:         'date_desc',
      villeFiltre: '',
      dateDebut:   (() => { const d = new Date(); d.setHours(12,0,0,0); return d.toISOString().split('T')[0] })(),
      dateFin:     (() => { const d = new Date(); d.setHours(12,0,0,0); return d.toISOString().split('T')[0] })(),
      loading:     true,
      erreur:      '',
      erreurReglement: '',
      annulationEnCours: '',
      reglementEnCours:  ''
    }
  },
  computed: {
    // Villes disponibles dans les tickets chargés
    villesDisponibles() {
      const villes = [...new Set(this.tickets.map(t => t.ville).filter(Boolean))]
      return villes.sort()
    },

    // Tous les tickets filtrés par date + recherche (sans filtre ville) pour les stats globales
    ticketsFiltresSansVille() {
      let list = [...this.tickets]
      if (this.dateDebut) list = list.filter(t => t.created_at.substring(0,10) >= this.dateDebut)
      if (this.dateFin)   list = list.filter(t => t.created_at.substring(0,10) <= this.dateFin)
      if (this.recherche.length >= 2) {
        const q = this.recherche.toLowerCase()
        list = list.filter(t =>
          t.prenom?.toLowerCase().includes(q) ||
          t.nom?.toLowerCase().includes(q) ||
          t.numero_carte?.toLowerCase().includes(q)
        )
      }
      return list
    },

    ticketsFiltres() {
      let list = [...this.ticketsFiltresSansVille]

      // Filtre ville
      if (this.villeFiltre) {
        list = list.filter(t => t.ville === this.villeFiltre)
      }

      // Tri
      list.sort((a, b) => {
        switch (this.tri) {
          case 'date_asc':     return new Date(a.created_at) - new Date(b.created_at)
          case 'date_desc':    return new Date(b.created_at) - new Date(a.created_at)
          case 'nom_asc':      return (a.nom || a.prenom).localeCompare(b.nom || b.prenom)
          case 'nom_desc':     return (b.nom || b.prenom).localeCompare(a.nom || a.prenom)
          case 'montant_desc': return b.montant_achat - a.montant_achat
          case 'points_desc':  return b.points_gagnes - a.points_gagnes
          default:             return 0
        }
      })

      return list
    },

    totalCA() {
      return this.ticketsFiltres.reduce((s, t) => s + parseFloat(t.montant || t.montant_achat || 0), 0)
    },
    totalPoints() {
      return this.ticketsFiltres.reduce((s, t) => s + parseInt(t.points_gagnes || 0), 0)
    },
    totalReductions() {
      return this.ticketsFiltres.filter(t => t.points_utilises > 0).length
    },
    totalCB() {
      return this.ticketsFiltres
        .filter(t => this.modePaiementNormalise(t.mode_paiement) === 'cb')
        .reduce((s, t) => s + parseFloat(t.montant || t.montant_achat || 0), 0)
    },
    totalEspeces() {
      return this.ticketsFiltres
        .filter(t => this.modePaiementNormalise(t.mode_paiement) === 'especes')
        .reduce((s, t) => s + parseFloat(t.montant || t.montant_achat || 0), 0)
    }
  },
  methods: {
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    },
    badgeType(t) {
      return { gain: 'badge-green', utilisation: 'badge-orange', bienvenue: 'badge-blue', sans_carte: 'badge-gray', vente_sans_carte: 'badge-gray' }[t] || 'badge-gray'
    },
    labelType(t) {
      return { gain: 'Achat', utilisation: 'Réduction', bienvenue: 'Bienvenue', sans_carte: 'Sans carte', vente_sans_carte: 'Sans carte' }[t] || t
    },
    clientNom(t) {
      if (t.source === 'sans_carte') return 'Vente sans carte'
      return t.prenom ? `${t.prenom} ${t.nom || ''}` : '—'
    },
    clientInitiales(t) {
      if (t.source === 'sans_carte') return 'SC'
      return `${t.prenom ? t.prenom[0] : '—'}${t.nom ? t.nom[0] : ''}`
    },
    ticketKey(t) {
      return `${t.source || (t.sans_carte ? 'sans_carte' : 'transaction')}-${t.id}`
    },
    modePaiementNormalise(mode) {
      return mode === 'cb' ? 'cb' : 'especes'
    },
    labelModePaiement(mode) {
      return this.modePaiementNormalise(mode) === 'cb' ? 'CB' : 'Espèces'
    },
    classeModePaiement(ticket) {
      return this.modePaiementNormalise(ticket.mode_paiement) === 'cb' ? 'badge-cb' : 'badge-especes'
    },
    modePaiementSuivant(ticket) {
      return this.modePaiementNormalise(ticket.mode_paiement) === 'cb' ? 'especes' : 'cb'
    },
    libelleBoutonReglement(ticket) {
      if (this.reglementEnCours === this.ticketKey(ticket)) return 'Mise à jour...'

      const prochainMode = this.labelModePaiement(this.modePaiementSuivant(ticket))
      return `Passer en ${prochainMode}`
    },
    libelleActionReglement(ticket) {
      const modeActuel = this.labelModePaiement(ticket.mode_paiement)
      const prochainMode = this.labelModePaiement(this.modePaiementSuivant(ticket))
      return `Passer le règlement du ticket ${ticket.id} de ${modeActuel} vers ${prochainMode}`
    },
    caParVille(ville) {
      return this.ticketsFiltresSansVille
        .filter(t => t.ville === ville)
        .reduce((s, t) => s + parseFloat(t.montant || t.montant_achat || 0), 0)
    },
    ticketsParVille(ville) {
      return this.ticketsFiltresSansVille.filter(t => t.ville === ville).length
    },
    setIntervalle(type) {
      const today = new Date()
      today.setHours(12, 0, 0, 0)
      const fmt = d => d.toISOString().split('T')[0]
      if (type === 'today') {
        this.dateDebut = fmt(today); this.dateFin = fmt(today)
      } else if (type === 'week') {
        const lundi = new Date(today)
        lundi.setDate(today.getDate() - today.getDay() + 1)
        lundi.setHours(12,0,0,0)
        this.dateDebut = fmt(lundi); this.dateFin = fmt(today)
      } else if (type === 'month') {
        const debut = new Date(today.getFullYear(), today.getMonth(), 1)
        debut.setHours(12,0,0,0)
        this.dateDebut = fmt(debut); this.dateFin = fmt(today)
      } else {
        this.dateDebut = ''; this.dateFin = ''
      }
    },
    async charger() {
      this.loading = true
      this.erreur = ''
      this.erreurReglement = ''
      try {
        const res    = await api.get('/clients/historique')
        this.tickets = res.data
      } catch {
        this.erreur = "Impossible de charger l'historique pour le moment."
      } finally {
        this.loading = false
      }
    },
    async changerReglement(ticket) {
      if (this.reglementEnCours) return

      const source = ticket.source || (ticket.sans_carte ? 'sans_carte' : 'transaction')
      const prochainMode = this.modePaiementSuivant(ticket)
      const labelActuel = this.labelModePaiement(ticket.mode_paiement)
      const labelNouveau = this.labelModePaiement(prochainMode)
      if (!window.confirm(`Changer le règlement de ${labelActuel} vers ${labelNouveau} ?`)) return

      const key = this.ticketKey(ticket)
      this.reglementEnCours = key
      this.erreurReglement = ''

      try {
        const res = await api.patch(`/clients/historique/${source}/${ticket.id}/reglement`, {
          mode_paiement: prochainMode
        })
        const ticketMisAJour = res.data?.ticket || {}
        this.tickets = this.tickets.map(t => {
          if (this.ticketKey(t) !== key) return t
          return {
            ...t,
            mode_paiement: ticketMisAJour.mode_paiement || prochainMode
          }
        })
      } catch (err) {
        this.erreurReglement = err.response?.data?.error || 'Impossible de modifier le règlement de ce ticket.'
      } finally {
        this.reglementEnCours = ''
      }
    },
    async annulerTicket(ticket) {
      if (this.annulationEnCours) return
      if (!window.confirm("Confirmer l'annulation de ce ticket ?")) return

      const source = ticket.source || (ticket.sans_carte ? 'sans_carte' : 'transaction')
      const key = this.ticketKey(ticket)
      this.annulationEnCours = key
      this.erreur = ''
      this.erreurReglement = ''

      try {
        await api.delete(`/clients/historique/${source}/${ticket.id}`)
        this.tickets = this.tickets.filter(t => this.ticketKey(t) !== key)
      } catch (err) {
        this.erreur = err.response?.data?.error || "Impossible d'annuler ce ticket."
      } finally {
        this.annulationEnCours = ''
      }
    }
  },
  mounted() { this.charger() }
}
</script>

<style scoped>
.historique-page {
  max-width: 1220px;
  padding: 32px 28px 44px;
  background: #F8FAFC;
}

.tickets-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 22px;
  margin-bottom: 22px;
}

.eyebrow {
  color: #1E6FDC;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.page-title {
  color: #0D1B3D;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  max-width: 650px;
  color: #64748B;
  font-size: 15px;
}

.hero-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(130px, 1fr));
  gap: 10px;
  min-width: 310px;
}

.hero-summary > div {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.summary-label {
  display: block;
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.hero-summary strong {
  color: #0D1B3D;
  font-size: 20px;
  font-weight: 800;
}

.card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  box-shadow: 0 16px 45px rgba(15, 23, 42, 0.07);
}

.filters-card {
  padding: 22px;
  margin-bottom: 18px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 14px;
  align-items: end;
}

.search-field {
  grid-column: span 2;
}

.label {
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 7px;
}

.input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  border: 1.5px solid #E2E8F0;
  border-radius: 12px;
  background: #FFFFFF;
  color: #0F172A;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.input:focus {
  border-color: #1E6FDC;
  box-shadow: 0 0 0 4px rgba(30, 111, 220, 0.12);
}

.input::placeholder {
  color: #94A3B8;
}

.quick-periods {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #E2E8F0;
}

.period-button,
.marche-stat {
  font: inherit;
}

.period-button {
  min-height: 38px;
  padding: 8px 15px;
  border: 1px solid #E2E8F0;
  border-radius: 999px;
  background: #F8FAFC;
  color: #0D1B3D;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.period-button:hover {
  border-color: #1E6FDC;
  background: #E8F1FD;
  color: #1E6FDC;
  transform: translateY(-1px);
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 18px;
}

.stat-card {
  padding: 18px;
  margin-bottom: 0;
}

.stat-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748B;
}

.stat-icon {
  position: relative;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 13px;
  background: #E8F1FD;
}

.stat-icon::before,
.stat-icon::after {
  content: '';
  position: absolute;
  box-sizing: border-box;
}

.icon-ticket::before {
  inset: 9px 11px;
  border: 2px solid #1E6FDC;
  border-radius: 4px;
}

.icon-revenue::before {
  width: 16px;
  height: 16px;
  left: 10px;
  top: 10px;
  border: 2px solid #1E6FDC;
  border-radius: 50%;
}

.icon-points {
  background: #DCFCE7;
}

.icon-points::before {
  width: 14px;
  height: 14px;
  left: 11px;
  top: 11px;
  background: #22C55E;
  clip-path: polygon(50% 0, 62% 34%, 98% 35%, 69% 56%, 79% 91%, 50% 70%, 21% 91%, 31% 56%, 2% 35%, 38% 34%);
}

.icon-reduction {
  background: #FEF3C7;
}

.icon-reduction::before {
  width: 18px;
  height: 18px;
  left: 9px;
  top: 9px;
  border: 2px solid #F59E0B;
  border-radius: 6px;
  transform: rotate(45deg);
}

.icon-cb {
  background: #E8F1FD;
}

.icon-cb::before {
  width: 20px;
  height: 14px;
  left: 8px;
  top: 11px;
  border: 2px solid #1E6FDC;
  border-radius: 4px;
}

.icon-cb::after {
  width: 12px;
  height: 2px;
  left: 12px;
  top: 16px;
  background: #1E6FDC;
  border-radius: 999px;
}

.icon-especes {
  background: #DCFCE7;
}

.icon-especes::before {
  width: 18px;
  height: 18px;
  left: 9px;
  top: 9px;
  border: 2px solid #16A34A;
  border-radius: 50%;
}

.icon-especes::after {
  content: '€';
  left: 14px;
  top: 8px;
  color: #16A34A;
  font-size: 13px;
  font-weight: 900;
}

.stat-label {
  font-size: 12px;
  font-weight: 700;
}

.stat-value {
  margin-top: 15px;
  color: #0D1B3D;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.ca-par-marche {
  padding: 22px;
  margin-bottom: 18px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.section-heading h2 {
  color: #0D1B3D;
  font-size: 18px;
  font-weight: 800;
}

.section-heading span {
  color: #64748B;
  font-size: 13px;
  font-weight: 600;
}

.marches-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 12px;
}

.marche-stat {
  display: grid;
  gap: 5px;
  width: 100%;
  padding: 15px;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  background: #F8FAFC;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.marche-stat:hover,
.marche-stat.actif {
  border-color: #1E6FDC;
  background: #FFFFFF;
  box-shadow: 0 12px 28px rgba(30, 111, 220, 0.1);
  transform: translateY(-1px);
}

.marche-stat.total {
  border-style: dashed;
}

.ms-ville {
  color: #64748B;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.ms-ca {
  color: #0D1B3D;
  font-size: 20px;
  font-weight: 800;
}

.ms-tickets {
  color: #64748B;
  font-size: 12px;
  font-weight: 600;
}

.tickets-card {
  padding: 0;
  overflow: hidden;
}

.tickets-heading {
  padding: 22px 22px 0;
}

.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1280px;
}

.table th {
  padding: 13px 16px;
  background: #F8FAFC;
  color: #64748B;
  border-top: 1px solid #E2E8F0;
  border-bottom: 1px solid #E2E8F0;
  font-size: 11px;
  font-weight: 800;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td {
  padding: 15px 16px;
  border-bottom: 1px solid #E2E8F0;
  color: #0F172A;
  vertical-align: middle;
  transition: background 0.2s ease;
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.table tbody tr:hover td {
  background: #F8FAFC;
}

.row-sans-carte td {
  background: #FFFCF2;
}

.row-sans-carte:hover td {
  background: #FFF7DB;
}

.align-right {
  text-align: right;
}

.client-cell {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 190px;
}

.client-cell strong {
  display: block;
  color: #0F172A;
  font-size: 13px;
  font-weight: 800;
}

.client-note {
  display: block;
  color: #64748B;
  font-size: 11px;
  font-weight: 600;
  margin-top: 2px;
}

.client-avatar-sm {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 14px;
  background: #0D1B3D;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 800;
}

.avatar-muted {
  background: #FEF3C7;
  color: #92400E;
}

.mono {
  color: #64748B;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
}

.montant {
  color: #0D1B3D;
  font-weight: 800;
  white-space: nowrap;
}

.points-plus {
  color: #15803D;
  font-weight: 800;
  white-space: nowrap;
}

.points-moins {
  color: #DC2626;
  font-weight: 800;
  white-space: nowrap;
}

.date {
  color: #64748B;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.badge,
.badge-ville,
.badge-paiement {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
}

.badge-ville,
.badge-blue,
.badge-cb {
  background: #E8F1FD;
  color: #1D4ED8;
  border-color: #BFDBFE;
}

.badge-green,
.badge-especes {
  background: #DCFCE7;
  color: #166534;
  border-color: #BBF7D0;
}

.badge-orange {
  background: #FEF3C7;
  color: #92400E;
  border-color: #FDE68A;
}

.badge-gray {
  background: #F1F5F9;
  color: #475569;
  border-color: #E2E8F0;
}

.muted-value {
  color: #94A3B8;
  font-weight: 700;
}

.inline-error {
  margin: 0 22px 14px;
  padding: 12px 14px;
  border: 1px solid #FCA5A5;
  border-radius: 12px;
  background: #FEF2F2;
  color: #B91C1C;
  font-size: 13px;
  font-weight: 800;
}

.paiement-button {
  min-width: 118px;
  min-height: 44px;
  gap: 4px;
  font: inherit;
  font-size: 11px;
  cursor: pointer;
  touch-action: manipulation;
  transition: box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.paiement-button:hover:not(:disabled),
.paiement-button:focus-visible {
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
  outline: none;
}

.paiement-button:disabled {
  cursor: wait;
  opacity: 0.7;
}

.ticket-action {
  min-height: 34px;
  padding: 8px 12px;
  border: 1px solid #FCA5A5;
  border-radius: 10px;
  background: #FEF2F2;
  color: #B91C1C;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
}

.ticket-action:hover:not(:disabled) {
  border-color: #EF4444;
  background: #FEE2E2;
}

.ticket-action:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.state-box {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 58px 22px 62px;
  text-align: center;
  color: #64748B;
}

.state-box strong {
  color: #0D1B3D;
  font-size: 17px;
  font-weight: 800;
}

.state-box p {
  max-width: 420px;
  color: #64748B;
  font-size: 14px;
}

.loader {
  width: 34px;
  height: 34px;
  border: 3px solid #E2E8F0;
  border-top-color: #1E6FDC;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.error-state strong {
  color: #B91C1C;
}

.tickets-mobile {
  display: none;
}

.ticket-mobile-card {
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  padding: 16px;
  background: #FFFFFF;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.ticket-mobile-card + .ticket-mobile-card {
  margin-top: 12px;
}

.sans-carte-card {
  background: #FFFCF2;
  border-color: #FDE68A;
}

.ticket-mobile-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.mobile-amount {
  color: #0D1B3D;
  font-size: 18px;
  font-weight: 800;
  white-space: nowrap;
}

.ticket-mobile-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.ticket-mobile-grid div {
  min-width: 0;
  padding: 11px;
  border-radius: 14px;
  background: #F8FAFC;
}

.ticket-mobile-grid span {
  display: block;
  color: #64748B;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
}

.ticket-mobile-grid strong {
  display: block;
  color: #0F172A;
  font-size: 12px;
  font-weight: 800;
  word-break: break-word;
}

.reglement-mobile-field {
  grid-column: span 2;
}

.reglement-mobile-field .badge-paiement {
  margin-top: 2px;
}

.reglement-mobile-action {
  grid-column: span 2;
}

.mobile-paiement-button {
  width: 100%;
  min-height: 44px;
  margin-top: 2px;
  border-radius: 12px;
  font-size: 13px;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.ticket-mobile-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 13px;
}

.mobile-action {
  width: 100%;
  margin-top: 13px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 1100px) {
  .stats-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .filters-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .search-field {
    grid-column: span 2;
  }
}

@media (max-width: 900px) {
  .tickets-hero {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-summary {
    min-width: 0;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .search-field {
    grid-column: span 2;
  }
}

@media (max-width: 760px) {
  .historique-page {
    padding: 18px 12px 104px;
  }

  .page-title {
    font-size: 30px;
  }

  .hero-summary,
  .stats-row,
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .search-field {
    grid-column: auto;
  }

  .filters-card,
  .ca-par-marche {
    padding: 16px;
  }

  .quick-periods {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }

  .period-button {
    width: 100%;
  }

  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .desktop-table {
    display: none;
  }

  .tickets-mobile {
    display: block;
    padding: 0 16px 16px;
  }

  .inline-error {
    margin: 0 16px 14px;
  }

  .paiement-button {
    width: 100%;
    min-width: 0;
  }

  .tickets-heading {
    padding: 18px 16px 16px;
  }
}

@media (max-width: 420px) {
  .ticket-mobile-top {
    flex-direction: column;
  }

  .ticket-mobile-grid {
    grid-template-columns: 1fr;
  }

  .reglement-mobile-field,
  .reglement-mobile-action {
    grid-column: auto;
  }

  .mobile-amount {
    font-size: 20px;
  }

  .ticket-mobile-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
